"use client";
import dynamic from "next/dynamic";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  toggleImportComponent,
  toggleReset,
  setFormHeadingToDefault,
  ShowForm,
  selectFormToHeading,
  setScale,
  selectScale,
} from "lib/redux/settingsSlice";
import { changeProfileStatus, resetResume } from "lib/redux/resumeSlice";
import Link from "next/link";
import Image from "next/image";
import { cx } from "lib/cx";
import { useTemplateComponents } from "components/Resume/templateUtils";
import { selectResume, setResume } from "lib/redux/resumeSlice";
import { selectSettings } from "lib/redux/settingsSlice";
import { useAppSelector } from "lib/redux/hooks";
// import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
// import { ResumeControlBarCSR } from "components/Resume/ResumeControlBar";
import CustomIcon from "components/CustomIcon";
import ShareIcon from "public/assets/ShareIcon.svg";
import {
  createResume,
  sendDownloadedResume,
  updateResume,
} from "api/resumeApi";
import { store } from "lib/redux/store";
import { saveStateToLocalStorage } from "lib/redux/local-storage";
import { setResumeCache } from "../utils/localStorageHelper";
import "react-toastify/dist/ReactToastify.css";
import { v4 as uuidv4 } from "uuid";
import {
  selectIsExplorePlansOverlayOpen,
  selectIsPremiumUser,
  selectUserProfilePhoto,
  setIsAuthenticationOpen,
  setIsExplorePlansOverlayOpen,
  setOAuthOpenedFromButton,
} from "lib/redux/uiSlice";
import DownloadButton from "components/DownloadButton";
import axios from "axios";
import { sanitizeResume } from "lib/utils/sanitizeResume";
import MobileMenu from "./MobileMenu";
import HomeIcon from "public/icons/home.svg";
import { ExperienceSwitch } from "./ExperienceSwitch";
import { ResumeAnalysis } from "lib/resume-scoring/resume-analysis/resume-analyse-types";
// import { Resume } from "lib/redux/types";
import {
  selectScoreAnalysis,
  selectExperienceLevel,
} from "lib/redux/scoreAnalysisSlice";
import { PDFManager } from "components/PDFManager";
import { ensureSpecificReactPDFFont } from "components/fonts/hooks";
import { usePathname } from "next/navigation";
import { useDebounce } from "components/useDebounce";

interface TopBarProps {
  setOpenShareResumeModal: (visible: boolean) => void;
  setHasAddedNewResume: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenSaveResumePopup: (visible: boolean) => void;
  handleAnalyseResume: () => void;
  calculateAndUpdateScore?: (
    analysisData: ResumeAnalysis,
    expLevel: "FRESHER" | "EXPERIENCED",
    // currentResume: Resume
  ) => void;
}

const TopBar: React.FC<TopBarProps> = ({
  setOpenShareResumeModal,
  handleAnalyseResume,
  calculateAndUpdateScore,
}) => {
  const scale = useAppSelector(selectScale);
  const setScaleValue = (value: number) => dispatch(setScale(value));
  const resume = useAppSelector(selectResume);
  const settings = useAppSelector(selectSettings);
  const dispatch = useDispatch();
  const formToHeading = useAppSelector(selectFormToHeading);
  const allHeadings = Object.keys(formToHeading) as ShowForm[];
  const { templateNameToDocument, template, templateList } =
    useTemplateComponents(resume, settings);

  const fileName = resume.profile.name + " - Resume";
  const accessToken = localStorage.getItem("access-token");
  const [logoRedirectURL, setLogoRedirectURL] = useState("/");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const scoreAnalysis = useAppSelector(selectScoreAnalysis);

  const menuRef = useRef<HTMLDivElement | null>(null);
  const analysis = useAppSelector((state) => state.scoreAnalysis?.analysis);
  const selectedExperienceLevel = useAppSelector(selectExperienceLevel);
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [ipAddress, setIpAddress] = useState<string | null>(null);
  const isPremiumUser = useAppSelector(selectIsPremiumUser);

  const pathname = usePathname();

  const isImageTemplateSelected = templateList.find(
    (t: any) => t.id === template
  )?.hasImage;
  const userProfilePhoto = useAppSelector(selectUserProfilePhoto);
  const UserProfilePhotoFile = new File(
    [userProfilePhoto as BlobPart],
    "profile-pic.png",
    {
      type: "image/png",
    }
  );

  // const [instance, update] = usePDF({ document });

  const stableTemplateNameToDocument = useMemo(() => {
    return templateNameToDocument;
  }, [resume, settings]);

  const computedDocument: JSX.Element | null = useMemo(() => {
    const templateDoc: JSX.Element = templateNameToDocument[template];
    if (!templateDoc) return null;

    if (userProfilePhoto) {
      // Return template with user profile photo
      return templateDoc;
    }
    return templateDoc;
  }, [userProfilePhoto, template, stableTemplateNameToDocument]);

  // key that changes when ANY part of the resume changes
  const documentKeyRaw = useMemo(() => {
    const resumeHash = JSON.stringify({
      profile: resume.profile,
      workExperiences: resume.workExperiences,
      educations: resume.educations,
      projects: resume.projects,
      skills: resume.skills,
      template: template,
      profilestatus: resume.profileStatus,
      language: resume.languages,
      custom: resume.custom,
      award: resume.awards,
      certifications: resume.certifications,
      userProfilePhoto: !!userProfilePhoto,
      settings: settings,
    });
    // Create a simple hash from the content
    let hash = 0;
    for (let i = 0; i < resumeHash.length; i++) {
      const char = resumeHash.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return `pdf-${template}-${Math.abs(hash)}`;
  }, [resume, template, userProfilePhoto, settings]);

  const documentKey = useDebounce(documentKeyRaw, 400);

  // State to hold the PDF instance
  const [pdfInstance, setPdfInstance] = useState<{
    url: string | null;
    loading: boolean;
    error: string | null;
  }>({
    url: null,
    loading: true,
    error: null,
  });

  const handlePDFReady = useCallback(
    (instance: {
      url: string | null;
      loading: boolean;
      error: string | null;
    }) => {
      setPdfInstance(instance);
    },
    []
  );

  // Get or create deviceId
  useEffect(() => {
    let storedDeviceId = localStorage.getItem("deviceId");
    if (!storedDeviceId) {
      storedDeviceId = uuidv4(); // Generate new UUID for deviceId
      localStorage.setItem("deviceId", storedDeviceId);
    }
    setDeviceId(storedDeviceId);
  }, []);

  // Getting IP Address
  useEffect(() => {
    const getIpAddress = async () => {
      try {
        const res = await fetch("https://api.ipify.org?format=json"); // Free API for IP address
        const data = await res.json();
        setIpAddress(data.ip);
      } catch (error) {
        console.error("Failed to get IP address:", error);
        return "";
      }
    };
    getIpAddress();
  }, []);

  useEffect(() => {
    setLogoRedirectURL(accessToken ? "/dashboard" : "/");
  }, [accessToken]);

  const toggleImportComponentState = () => {
    dispatch(toggleImportComponent()); // Dispatching the action to toggle the state
  };

  const handleLogout = () => {
    localStorage.removeItem("access-token");
    window.location.href = "/resume-builder";
  };

  const handleReset = () => {
    dispatch(toggleReset());
    dispatch(resetResume());
    dispatch(setFormHeadingToDefault({ field: allHeadings }));
  };

  const handleSaveResume = async () => {
    dispatch(setOAuthOpenedFromButton("save"));
    try {
      const access_token = localStorage.getItem("access-token");
      if (!access_token) {
        dispatch(setIsAuthenticationOpen(true));
        localStorage.setItem("auth-overlay", "false");
        return;
      }
      const sanitizedResume = sanitizeResume(resume);
      if (resume.id) {
        const updatedResume = await updateResume(
          sanitizedResume,
          resume.id,
          userProfilePhoto!
        );
        dispatch(setResume(updatedResume));

        // CRITICAL: Update both localStorage keys to keep them synchronized
        setResumeCache({ resume: updatedResume });
        
        // Update localStorage with correct key
        const currentState = store.getState();
        saveStateToLocalStorage({
          ...currentState,
          resume: updatedResume,
        });
      } else {
        const createdResume = await createResume(
          sanitizedResume,
          userProfilePhoto!
        );
        dispatch(setResume(createdResume));

        // CRITICAL: Update both localStorage keys to keep them synchronized
        setResumeCache({ resume: createdResume });
        
        // Update localStorage with correct key
        const currentState = store.getState();
        saveStateToLocalStorage({
          ...currentState,
          resume: createdResume,
        });
      }
      dispatch(changeProfileStatus("IGNORED"));
    } catch (error: any) {
      if (error.code === "TOKEN_EXPIRED") {
        dispatch(setIsAuthenticationOpen(true));
      }
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        dispatch(setResume({ ...resume, id: undefined }));
        const createdResume = await createResume(resume, userProfilePhoto!);
        dispatch(setResume(createdResume));

        // CRITICAL: Update both localStorage keys to keep them synchronized
        setResumeCache({ resume: createdResume });
        
        // Update localStorage after error recovery
        const currentState = store.getState();
        saveStateToLocalStorage({
          ...currentState,
          resume: createdResume,
        });
      }
    }
  };

  const handleDownloadResume = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!accessToken) {
      e.preventDefault();
      dispatch(setIsAuthenticationOpen(true));
      return;
    }
  };

  const handleSendDownloadedResume = async () => {
    const sanitizedResume = sanitizeResume(resume);
    try {
      const payload = {
        ...sanitizedResume,
        deviceId: deviceId,
        ipAddress: ipAddress,
        ua: navigator.userAgent,
        screenSize: `${window.screen.width}x${window.screen.height}`,
      };
      await sendDownloadedResume(payload);
    } catch (error) {
      console.debug(error);
    }
  };

  const handleDownloadResumeWithoutAuth = () => {
    if (!accessToken) {
      // setOpenSaveResumePopup(true);
      dispatch(setIsExplorePlansOverlayOpen(true));
    }
    handleSendDownloadedResume();
    !isPremiumUser && dispatch(setIsExplorePlansOverlayOpen(true));
    return;
  };

  const handleResumeShare = () => {
    if (!accessToken) {
      dispatch(setOAuthOpenedFromButton("share"));
      dispatch(setIsAuthenticationOpen(true));
      return;
    }
    setOpenShareResumeModal(true);
  };

  // REMOVED: The problematic useEffect that causes "Eo is not a function" error
  // The usePDF hook will automatically update when the document prop changes
  {
    /*// Hook to update pdf when document changes
  useEffect(() => {
    // @ts-ignore: Unreachable code error
    update();
  }, [update, document, userProfilePhoto]); 
  */
  }
  return (
    <>
      {ensureSpecificReactPDFFont(settings.fontFamily)}
      <PDFManager
        key={documentKey}
        document={computedDocument}
        onPDFReady={handlePDFReady}
      />
      <header
        aria-label="Site Header"
        className={cx(
          "mx-4 mt-3 flex  h-[60px] items-center rounded-2xl bg-white px-2 shadow-sm shadow-[#0000000D] md:px-4 lg:mx-8"
        )}
      >
        <div className="relative flex h-10 w-full items-center justify-between">
          <div className="flex w-full items-center justify-between lg:justify-start lg:gap-12 xl:gap-24 2xl:gap-36">
            <Link href={logoRedirectURL}>
              {!accessToken || !isPremiumUser ? (
                <>
                  <span className="sr-only">Dashboard</span>
                  <div className="2xl:w-[200px]">
                    <Link
                      href="/premium-subscription"
                      className="group flex h-[50px] w-[50px] items-center justify-between rounded-full bg-gradient-to-r from-[#C7F866]  to-[#7DBC00] px-[10px] py-[7px] transition-all hover:w-[200px] hover:px-3 lg:h-[50px] lg:w-[50px]"
                    >
                      <Image
                        src="/icons/diamond.svg"
                        height={45}
                        width={45}
                        alt="premium logo"
                        className="h-[33px] w-[42px]"
                      />
                      <span className="duration-400 hidden flex-nowrap whitespace-nowrap text-lg font-semibold text-white transition-all group-hover:flex">
                        Get premium
                      </span>
                    </Link>
                  </div>
                </>
              ) : (
                <>
                  {/* <span className="">ResumeMate</span> */}
                  <div className="flex items-center gap-1 lg:gap-2">
                    <div className="relative h-[28px] w-[24px] md:h-[35px] md:w-[30px] lg:h-[42px] lg:w-[36px] 2xl:h-[51px] 2xl:w-[44px]">
                      <Image
                        src="/assets/resumemateLogo.png"
                        alt="ResumeMate"
                        fill={true}
                      />
                    </div>
                    <div className="hidden font-montserrat text-[10px] font-semibold leading-3 md:text-xs md:leading-3 lg:text-xs lg:leading-3 xl:text-sm xl:leading-4 2xl:text-base 2xl:leading-4 [@media(min-width:1180px)]:block">
                      <span className="text-[#1C75BC]">Resume</span> <br />
                      <span className="text-[#27AAE1]">Mate.io</span>
                    </div>
                  </div>
                </>
              )}
            </Link>
            <div className="flex gap-2">
              <Link
                href="/resume-builder"
                className=" hidden items-center justify-center gap-1 rounded-full border border-gray-200 bg-white px-6 py-3 text-xs shadow-xl lg:flex xl:gap-2 xl:px-8 xl:text-sm"
              >
                <CustomIcon iconName="edit" />
                Edit in builder
              </Link>
              {/* Web view: Original Recheck score button */}
              {pathname === "/score-check" && (
                <button
                  onClick={handleAnalyseResume}
                  className="group relative hidden overflow-hidden rounded-full bg-gradient-to-r from-[#C24AF9] via-[#F785F9] to-[#EF5AF0] px-8 py-3 font-semibold text-white shadow-lg transition-shadow duration-300 hover:shadow-xl lg:flex"
                >
                  <div className="animate-shine absolute inset-0 -translate-x-full skew-x-12 bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-1000 group-hover:translate-x-full"></div>

                  {/* Button content */}
                  <div className="relative flex items-center gap-2">
                    {/* Refresh icon */}
                    <Image
                      src="/icons/checkScore.svg"
                      alt="check score icon"
                      width={20}
                      height={20}
                    />
                    <span>Recheck score</span>
                  </div>

                  {/* Additional shine animation styles */}
                  <style jsx>{`
                    @keyframes shine {
                      0% {
                        transform: translateX(-100%) skewX(-12deg);
                      }
                      100% {
                        transform: translateX(200%) skewX(-12deg);
                      }
                    }

                    .animate-shine {
                      animation: shine 2s infinite;
                    }
                  `}</style>
                </button>
              )}

              {/* Mobile only: Menu button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="flex items-center justify-center rounded-full bg-[#438cf7] p-2 lg:hidden"
              >
                <CustomIcon iconName="menu" />
              </button>

              {/* <button
              className="rounded-3xl pwindowx-4 py-2 text-sm shadow-[1.9px_3px_5.8px_0px_rgba(0,0,0,0.05)]"
              onClick={handleReset}
            >
              Reset All
            </button> */}
              {/* {accessToken && (
              <button
                className="hidden rounded-3xl px-4 py-2 shadow-[1.9px_3px_5.8px_0px_rgba(0,0,0,0.05)] lg:block"
                onClick={handleLogout}
              >
                Log Out
              </button>
            )} */}
            </div>
          </div>
          <nav className="hidden items-center gap-2 lg:flex">
            {/* {pathname !== "/score-check" && (
              <div className="">
                <ResumeControlBarCSR
                  scale={scale}
                  setScale={setScaleValue}
                  documentSize={settings.documentSize}
                  document={templateNameToDocument[template]}
                  fileName={resume.profile.name + " - Resume"}
                />
              </div>
            )} */}
            {/* <button
              className="flex items-center rounded-full p-2 shadow-[1.9px_3px_5.8px_0px_rgba(0,0,0,0.05)]"
              onClick={handleResumeShare}
            >
              <ShareIcon />
            </button> */}

            {pathname !== "/score-check" && (
              <button
                className="flex items-center gap-1 rounded-3xl px-4 py-2 shadow-[1.9px_3px_5.8px_0px_rgba(0,0,0,0.05)]"
                onClick={handleSaveResume}
              >
                <CustomIcon iconName="save" />
                Save
              </button>
            )}

            {/* Experience level switch */}
            {scoreAnalysis && (
              <ExperienceSwitch
                selectedExperience={selectedExperienceLevel}
                onChange={(expLevel) => {
                  if (calculateAndUpdateScore && analysis ) {
                    calculateAndUpdateScore(analysis, expLevel);
                  }
                }}
                className="mx-2"
              />
            )}

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="flex items-center justify-center rounded-full bg-[#438cf7] p-2"
            >
              <CustomIcon iconName="menu" />
            </button>
            {isMenuOpen && (
              <div
                ref={menuRef}
                className={`absolute right-5 top-12 z-10 flex flex-col items-start justify-center gap-y-4 rounded-xl bg-white p-4 shadow-xl transition-all duration-500 ease-in-out
   ${
     isMenuOpen
       ? "translate-y-0 opacity-100"
       : "pointer-events-none -translate-y-4 opacity-0"
   }`}
              >
                <Link
                  href="/"
                  className="flex items-center justify-center gap-x-2 py-2 transition-colors hover:text-blue-500"
                >
                  {/* <Image
                  className="bg-gradient-to-r from-[#88c4fc] to-[#1b91fe] p-1 rounded-full"
                  src="/icons/home.svg"
                  alt="home icon"
                  width={30}
                  height={30}
                /> */}
                  {/* <CustomIcon iconName="home" /> */}
                  <HomeIcon className="h-[30px] w-[30px] rounded-full bg-gradient-to-r from-[#88c4fc] to-[#1b91fe] p-1" />
                  <span>Home</span>
                </Link>

                {accessToken && (
                  <Link
                    href="/dashboard"
                    className="flex items-center justify-center gap-x-2 py-2 transition-colors hover:text-blue-500"
                  >
                    <Image
                      className="rounded-full bg-gradient-to-r from-[#88c4fc] to-[#1b91fe] p-1"
                      src="/assets/dashboardIcon.svg"
                      alt="dashboard icon"
                      width={30}
                      height={30}
                    />
                    <span>Dashboard</span>
                  </Link>
                )}

                {accessToken ? (
                  <button
                    onClick={handleLogout}
                    className="flex items-center justify-center gap-x-2 py-2 transition-colors hover:text-blue-500"
                  >
                    <Image
                      className="rounded-full bg-gradient-to-r from-[#88c4fc] to-[#1b91fe] p-1"
                      src="/icons/logout.svg"
                      alt="logout icon"
                      width={30}
                      height={30}
                    />
                    <span>Sign out</span>
                  </button>
                ) : (
                  <button
                    onClick={() => dispatch(setIsAuthenticationOpen(true))}
                    className="flex items-center justify-center gap-x-2 py-2 transition-colors hover:text-blue-500"
                  >
                    <Image
                      className="rounded-full bg-gradient-to-r from-[#88c4fc] to-[#1b91fe] p-1"
                      src="/icons/logout.svg"
                      alt="login icon"
                      width={30}
                      height={30}
                    />
                    <span>Sign in</span>
                  </button>
                )}
                {/* <button
                  onClick={handleSaveResume}
                  className="flex items-center justify-center gap-x-2 py-2 transition-colors hover:text-blue-500"
                >
                  <Image
                    className="rounded-full bg-gradient-to-r from-[#88c4fc] to-[#1b91fe] p-1"
                    src="/icons/saveDark.svg"
                    alt="logout icon"
                    width={30}
                    height={30}
                  />
                  <span>Save</span>
                </button> */}
                <button
                  onClick={handleResumeShare}
                  className="flex items-center justify-center gap-x-2 py-2 transition-colors hover:text-blue-500"
                >
                  <span className="flex items-center gap-2 rounded-full bg-gradient-to-r from-[#88c4fc] to-[#1b91fe] p-2 shadow-[1.9px_3px_5.8px_0px_rgba(0,0,0,0.05)]">
                    <ShareIcon className="invert" />
                  </span>
                  <span>Share</span>
                </button>
                <DownloadButton
                  url={pdfInstance.url}
                  fileName={fileName}
                  onDownload={handleDownloadResumeWithoutAuth}
                />
              </div>
            )}
            {/* </button> */}
          </nav>
        </div>

        {/* Mobile Menu (only shown on mobile) */}
        <MobileMenu
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
          accessToken={accessToken}
          handleLogout={handleLogout}
          handleSaveResume={handleSaveResume}
          handleResumeShare={handleResumeShare}
          handleAnalyseResume={handleAnalyseResume}
          calculateAndUpdateScore={calculateAndUpdateScore}
        />
      </header>
    </>
  );
};

/**
 * Load TopNavBar client side since it uses usePDF, which is a web specific API
 */
export const ScoreNavbar = dynamic(() => Promise.resolve(TopBar), {
  ssr: false,
  loading: () => (
    // Skeleton
    <div className="mx-4 mt-3 lg:mx-8">
      <div className="flex h-10 items-center rounded-2xl bg-white px-2 shadow-sm shadow-[#0000000D] md:px-4 lg:h-[60px]">
        <div className="flex w-full items-center justify-between">
          {/* Left side */}
          <div className="flex items-center gap-4">
            <div className="h-8 w-32 animate-pulse rounded bg-gray-200 lg:h-10 lg:w-40" />
          </div>
          {/* Right side */}
          <div className="flex items-center gap-2 md:gap-3">
            <div className="hidden h-8 w-24 animate-pulse rounded bg-gray-200 md:block lg:h-9 lg:w-28" />
            <div className="hidden h-8 w-24 animate-pulse rounded bg-gray-200 md:block lg:h-9 lg:w-28" />
            <div className="h-8 w-8 animate-pulse rounded-full bg-gray-200 lg:h-10 lg:w-10" />
          </div>
        </div>
      </div>
    </div>
  ),
});
